/**
 * Overrides the company fee settings
 */

import { z } from 'zod';

import { NonNegativeMonetaryValue } from '../validations/fields';

export const PrepaymentPenaltyPlanType = z.enum(['percent', 'fixed']);
export type PrepaymentPenaltyPlanType = z.infer<typeof PrepaymentPenaltyPlanType>;

/**
 * Eng note: If the prepayment penalty fee is set on the loan overrides, we aren't actually using it.
 * For now, it just displays a message in the UI when creating a payoff quote.
 * Work to implement this fee override is noted by the product team in the automated payoffs project.
 * When it's time to implement, new boarding fields + validation will need to be added to make this useable
 */
export const LoanPrepaymentPenaltyFee = z.object({
  flag: z.boolean(), // indicates whether to override the company setting
  planType: PrepaymentPenaltyPlanType.optional(), // followup as noted above: this will need to be refactored to a union like the lateFee
  expirationMonths: z.int().positive().optional(),
  planDescription: z.string().optional(), // display-only copy
});
export type LoanPrepaymentPenaltyFee = z.infer<typeof LoanPrepaymentPenaltyFee>;

export const LoanLateFeeType = z.enum(['percent', 'fixed']);
export type LoanLateFeeType = z.infer<typeof LoanLateFeeType>;

export const LoanLateFeePercent = z.object({
  type: z.literal(LoanLateFeeType.enum.percent),
  decimalCharge: z.number().nonnegative(),
  minAmount: NonNegativeMonetaryValue.optional(),
  maxAmount: NonNegativeMonetaryValue.optional(),
});
export type LoanLateFeePercent = z.infer<typeof LoanLateFeePercent>;

export const LoanLateFeeFixed = z.object({
  type: z.literal(LoanLateFeeType.enum.fixed),
  amount: NonNegativeMonetaryValue,
});
export type LoanLateFeeFixed = z.infer<typeof LoanLateFeeFixed>;

export const LoanLateFee = z.discriminatedUnion('type', [LoanLateFeePercent, LoanLateFeeFixed]);
export type LoanLateFee = z.infer<typeof LoanLateFee>;

// TERMINATION FEE
export const LoanTerminationFeeType = z.enum(['percent', 'fixed', 'disabled']);
export type LoanTerminationFeeType = z.infer<typeof LoanTerminationFeeType>;

export const LoanTerminationFeePercent = z.object({
  type: z.literal(LoanTerminationFeeType.enum.percent),
  flag: z.literal(true),
  percentPrincipal: z.number().nonnegative(),
  percentMonths: z.int().positive(),
});
export type LoanTerminationFeePercent = z.infer<typeof LoanTerminationFeePercent>;

export const LoanTerminationFeeFixed = z.object({
  type: z.literal(LoanTerminationFeeType.enum.fixed),
  flag: z.literal(true),
  fixedAmount: NonNegativeMonetaryValue,
  fixedMonths: z.int().positive(),
  perProperty: z.boolean(),
});
export type LoanTerminationFeeFixed = z.infer<typeof LoanTerminationFeeFixed>;

/**
 * @description The disabled type is used to allow users to set the flag to false without providing other data.
 * getFeeInput.ts discards the remaining fields.
 */
export const LoanTerminationFeeDisabled = z.object({
  type: z.literal(LoanTerminationFeeType.enum.disabled),
  flag: z.literal(false),
});
export type LoanTerminationFeeDisabled = z.infer<typeof LoanTerminationFeeDisabled>;

export const LoanTerminationFee = z.discriminatedUnion('type', [
  LoanTerminationFeePercent,
  LoanTerminationFeeFixed,
  LoanTerminationFeeDisabled,
]);
export type LoanTerminationFee = z.infer<typeof LoanTerminationFee>;

// PRINCIPAL LATE FEE
export const LoanPrincipalLateFeeType = z.enum(['percent', 'fixed', 'disabled']);
export type LoanPrincipalLateFeeType = z.infer<typeof LoanPrincipalLateFeeType>;

export const LoanPrincipalLateFeePercent = z.object({
  type: z.literal(LoanPrincipalLateFeeType.enum.percent),
  flag: z.literal(true),
  percentPrincipal: z.number().nonnegative(),
  percentDays: z.number().positive().int(),
});
export type LoanPrincipalLateFeePercent = z.infer<typeof LoanPrincipalLateFeePercent>;

export const LoanPrincipalLateFeeFixed = z.object({
  type: z.literal(LoanPrincipalLateFeeType.enum.fixed),
  flag: z.literal(true),
  fixedAmount: NonNegativeMonetaryValue,
  fixedDays: z.number().positive().int(),
  perProperty: z.boolean(),
});
export type LoanPrincipalLateFeeFixed = z.infer<typeof LoanPrincipalLateFeeFixed>;

/**
 * @description The disabled type is used to allow users to set the flag to false without providing other data.
 * getFeeInput.ts discards the remaining fields.
 */
export const LoanPrincipalLateFeeDisabled = z.object({
  type: z.literal(LoanPrincipalLateFeeType.enum.disabled),
  flag: z.literal(false),
});
export type LoanPrincipalLateFeeDisabled = z.infer<typeof LoanPrincipalLateFeeDisabled>;

export const LoanPrincipalLateFee = z.discriminatedUnion('type', [
  LoanPrincipalLateFeePercent,
  LoanPrincipalLateFeeFixed,
  LoanPrincipalLateFeeDisabled,
]);
export type LoanPrincipalLateFee = z.infer<typeof LoanPrincipalLateFee>;

/**
 * Overrides company fee settings
 */
export const LoanFees = z.object({
  lateFee: LoanLateFee.optional(),
  prepaymentPenalty: LoanPrepaymentPenaltyFee.optional(),
  terminationFee: LoanTerminationFee.optional(),
  principalLateFee: LoanPrincipalLateFee.optional(),
});
export type LoanFees = z.infer<typeof LoanFees>;
